import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Select as MantineSelect, SelectProps } from '@mantine/core';
import { ReactComponent as IconSearch } from './IconSearch.svg';
import { SelectWrapper } from './styles';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import { IoIosArrowDown } from 'react-icons/io';
import { ErrorMessage } from '@hookform/error-message';
import { useMediaQuery } from '@mantine/hooks';

interface IProps extends SelectProps {
  name: string;
  label?: string;
  isLoading?: boolean;
  registerOptions?: RegisterOptions;
  isrequired?: boolean;
  icon?: React.ReactNode;
  nothingFound?: React.ReactNode;
  showAstric?: boolean;
  withinPortal?: boolean;
}

export function Select({
  name,
  label,
  icon = <IoIosArrowDown />,
  rightSection = <IconSearch />,
  nothingFound,
  data,
  onSearchChange,
  isLoading,
  searchable = false,
  isrequired,
  withinPortal= false,
  ...rest
}: IProps) {
  const { formState } = useFormContext();
  const { errors } = formState;
  /* ------------------------------- Local State ------------------------------ */
  const [isSearched, setIsSearched] = useState(false);
  /* ---------------------------------- Hooks --------------------------------- */
  const { formatMessage } = useIntl();

  /* ------------------------------ Derived State ----------------------------- */
  const notFoundContent = useMemo(() => {
    if (data === undefined || isLoading) return;
    if (nothingFound) return nothingFound;
    return <p>{formatMessage({ id: 'noContentFound' })}</p>;
  }, [data, nothingFound, isLoading, formatMessage]);

  function handleOnSearch(value: string) {
    if (data === undefined || data.length === 0) return;
    setIsSearched(value !== '');
    onSearchChange && onSearchChange(value);
  }

  const isMobile = useMediaQuery('(max-width: 768px)');

  return (
    <SelectWrapper className="wrapper">
      <div className="label">
        {label && <p>{label}</p>}
        <MantineSelect
          {...rest}
          data={data}
          rightSection={icon}
          nothingFound={notFoundContent}
          onSearchChange={handleOnSearch}
          searchable={searchable}
          className={`${errors[name] ? 'hasError' : ''}`}
          styles={() => ({
            item: {
              wordBreak: 'normal',
              fontSize: '13px',
              textAlign:
                localStorage.getItem('lang') === 'en' ? 'left' : 'right',
              '&[data-selected="true"]': {
                backgroundColor: '#000',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#000',
                },
              },
            },
            dropdown: {
              zIndex: 9999,
            },
            rightSection: {
              pointerEvents: 'none',
            },
            input: {
              cursor: 'pointer',
            }
          })}
          withinPortal={!isMobile || withinPortal}
          portalProps={{
            style: { zIndex: 9999 },
          }}
        />
      </div>
      {!!errors && !!errors[name] && (
        <div className="ErrorMessageStyle">
          <ErrorMessage
            errors={errors}
            name={name}
            render={({ message }) => <p>{message}</p>}
          />
        </div>
      )}
    </SelectWrapper>
  );
}
